// SF5 — Search Visibility Score.
//
// The PRD is explicit that a score a seller cannot explain is one they stop
// trusting the first time it moves against them. So the weights are on screen,
// each component is shown both as its rank and as the raw reading behind it,
// and what the score is relative to is stated rather than implied.
import React from 'react';
import s from '../Visibility.module.css';
import { COMPONENT_LABEL, COMPONENT_MEANING, VERDICT_CLASS, num, pct } from '../format';

export default function Score({ data }) {
  if (!data) return <div className={s.empty}>Loading…</div>;
  const scored = (data.products || []).filter((p) => p.scored);
  const thin = (data.products || []).filter((p) => !p.scored);

  if (!scored.length) {
    return (
      <div className={s.empty}>
        <p>No product carries enough search terms to score.</p>
        <p className={s.hint}>
          A composite built on a handful of terms is noise presented as insight, so a
          product needs at least {data.minimum_terms} terms before it is scored.
          {thin.length > 0 && <> {num(thin.length)} products have search data but fall
            below that.</>}
        </p>
      </div>
    );
  }

  return (
    <>
      <div className={s.section}>
        <div className={s.sectionTitle}>How the score is built</div>
        <div className={s.weightRow}>
          {Object.entries(data.weights).map(([key, weight]) => (
            <div key={key} className={s.weight}>
              <div className={s.weightHead}>
                <span className={s.weightName}>{COMPONENT_LABEL[key] || key}</span>
                <span className={s.weightValue}>{pct(weight, 0)}</span>
              </div>
              <div className={s.barTrack}>
                <div className={s.barFill} style={{ width: `${weight * 100}%` }} />
              </div>
              <span className={s.weightMeaning}>{COMPONENT_MEANING[key]}</span>
            </div>
          ))}
        </div>
        <p className={s.hint} style={{ marginTop: 10 }}>
          Each component is ranked across your own catalogue before its weight applies, so
          the score says where a product sits <strong>among your products</strong> — the only
          comparison this data supports, since Amazon publishes no cross-seller benchmark.
          The raw reading behind each rank is shown on the row.
          {data.compared_to
            ? <> Traffic is measured against {data.compared_to}.</>
            : <> No earlier window is collected, so Traffic scores neutral for every
              product rather than rewarding or penalising absent history.</>}
        </p>
      </div>

      <div className={s.tableWrap} style={{ marginTop: 14 }}>
        <table className={s.table}>
          <thead>
            <tr>
              <th>Product</th>
              <th className={s.numeric}>Score</th>
              <th>Verdict</th>
              {Object.keys(data.weights).map((k) => (
                <th key={k} className={s.numeric}>{COMPONENT_LABEL[k]}</th>
              ))}
              <th className={s.numeric}>Terms</th>
            </tr>
          </thead>
          <tbody>
            {scored.map((p) => (
              <tr key={p.asin}>
                <td className={s.asin}>{p.asin}</td>
                <td className={s.numeric}><strong>{p.score}</strong></td>
                <td>
                  <span className={`${s.verdict} ${s[VERDICT_CLASS[p.verdict]] || ''}`}>
                    {p.verdict}
                  </span>
                </td>
                {Object.keys(data.weights).map((k) => (
                  <td key={k} className={s.numeric}>
                    <span className={s.component}>
                      <span className={s.componentBar}
                            style={{ width: `${(p.components[k] || 0) * 100}%` }} />
                      <span className={s.componentNum}>
                        {Math.round((p.components[k] || 0) * 100)}
                      </span>
                    </span>
                    <span className={s.reading}>
                      {k === 'reach' ? pct(p.readings.reach, 2)
                        : k === 'attention' ? `${(p.readings.attention || 0).toFixed(2)}x`
                        : k === 'breadth' ? pct(p.readings.breadth, 0)
                        : p.traffic_baseline ? pct(p.readings.traffic, 0) : 'neutral'}
                    </span>
                  </td>
                ))}
                <td className={s.numeric}>{num(p.terms)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!!thin.length && (
        <div className={s.section} style={{ marginTop: 14 }}>
          <div className={s.sectionTitle}>Too little data to score ({thin.length})</div>
          <p className={s.hint}>
            These appear in search but on too few terms to build a composite from. That is
            insufficient data, not poor visibility.
          </p>
          <div className={s.chipRow}>
            {thin.slice(0, 20).map((p) => (
              <span key={p.asin} className={s.chip}>
                {p.asin} <span className={s.muted}>{p.terms} terms</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
